import { CheckCircle, Edit, ArrowRight, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { Card } from "@/components/ui/card";
import { SetupAnalysisResult } from '@/types/agent/setup';
import { GenieAvatar } from "@/components/GenieAvatar";
import { DEFAULT_BRANDING } from '@/types/ui/branding';

interface ImageAnalysisResultsProps {
  analysis: SetupAnalysisResult;
  uploadedImage: string;
  onProceedToConfirmation: () => void;
  onRetry: () => void;
}

const ImageAnalysisResults = ({
  analysis,
  uploadedImage,
  onProceedToConfirmation,
  onRetry,
}: ImageAnalysisResultsProps) => {
  const branding = analysis.branding || DEFAULT_BRANDING;

  return (
    <div className="w-full max-w-5xl mx-auto p-6">
      <div className="text-center mb-8">
        <h2 className="text-3xl md:text-4xl font-bold mb-2 bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] bg-clip-text text-transparent">
          Image Analysis Complete
        </h2>
        <p className="text-base text-gray-600">
          Review what we found and customize before creating your Genie
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-8">
        {/* Left: Uploaded Image */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <ImageIcon className="h-5 w-5 text-blue-500" />
            Your Uploaded Image
          </h3>
          <img
            src={uploadedImage}
            alt="Uploaded"
            className="w-full h-auto rounded-lg max-h-96 object-contain border-2 border-gray-200"
          />
        </Card>

        {/* Right: Extracted Info */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-500" />
            What We Found
          </h3>

          <div className="space-y-4">
            {/* Genie Preview */}
            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
              <GenieAvatar
                color={branding.primaryColor}
                secondaryColor={branding.secondaryColor}
                gradientEnabled={branding.gradientEnabled}
                gradientAngle={branding.gradientAngle}
                avatarBackgroundOpacity={branding.avatarBackgroundOpacity}
                customAvatarUrl={branding.logoUrl || branding.customAvatarUrl}
                speaking={false}
                listening={false}
                isConnecting={false}
                hasCustomBranding={!!(branding.logoUrl || branding.customAvatarUrl)}
                size={80}
              />
              <div className="flex-1">
                <h4 className="font-bold text-xl text-gray-900">{analysis.genieName}</h4>
                <p className="text-sm text-gray-600">{analysis.category || "General"}</p>
              </div>
            </div>

            {/* Key Details */}
            <div className="space-y-3">
              <DetailRow label="Business Type" value={analysis.useCase} />
              {analysis.brand && <DetailRow label="Brand" value={analysis.brand} />}
              {analysis.description && <DetailRow label="Description" value={analysis.description} />}
              {analysis.supportEmails && analysis.supportEmails.length > 0 && (
                <DetailRow label="Contact Email" value={analysis.supportEmails[0]} />
              )}
              {analysis.language && <DetailRow label="Language" value={analysis.language} />}
            </div>

            {/* Confidence Score */}
            <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-blue-900">Analysis Confidence</span>
                <span className="text-sm font-bold text-blue-700">
                  {Math.round((analysis.confidence || 0) * 100)}%
                </span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all"
                  style={{ width: `${(analysis.confidence || 0) * 100}%` }}
                />
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Suggested Genie Personality */}
      <Card className="p-6 mb-6">
        <h3 className="text-lg font-semibold mb-3">Suggested Personality & Voice</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
            <p className="text-sm font-medium text-purple-900 mb-1">Voice Style</p>
            <p className="text-sm text-purple-700">{analysis.voicePreference}</p>
          </div>
          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <p className="text-sm font-medium text-green-900 mb-1">First Message</p>
            <p className="text-sm text-green-700 italic">"{analysis.firstMessage}"</p>
          </div>
        </div>
        {analysis.systemPrompt && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm font-medium text-gray-900 mb-2">System Instructions</p>
            <p className="text-xs text-gray-600 line-clamp-3">{analysis.systemPrompt}</p>
          </div>
        )}
      </Card>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          onClick={onProceedToConfirmation}
          className="flex-1 h-12 text-white font-semibold shadow-lg hover:shadow-xl transition-all hover:scale-105"
          style={{
            background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)",
          }}
        >
          <Edit className="mr-2 h-4 w-4" />
          Review & Customize Settings
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          onClick={onRetry}
          className="h-12 border-2 border-gray-300 hover:bg-gray-50 font-semibold transition-all hover:scale-105"
        >
          Upload Different Image
        </Button>
      </div>

      <div className="mt-4 text-center">
        <p className="text-xs text-gray-500">
          All settings can be customized in the next step before creating your Genie
        </p>
      </div>
    </div>
  );
};

const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <div className="flex justify-between items-start py-2 border-b border-gray-200">
    <span className="text-sm font-medium text-gray-600">{label}:</span>
    <CopyableEmail email={value} className="text-sm text-gray-900 text-right max-w-[60%]" />
  </div>
);

export default ImageAnalysisResults;
